/**
 * WebMCP — exposes a handful of real storefront actions to in-browser AI
 * agents via `navigator.modelContext.provideContext()`. This is an
 * experimental browser API (not shipped everywhere yet); calling this
 * simply does nothing on browsers that don't support it — no polyfill,
 * no error.
 *
 * Tools mirror the "skills" already declared for HTTP-based agents in
 * /.well-known/agents.json (search, product lookup, shipping quote), plus
 * one that only makes sense in-browser: adding to the *current* session's
 * cart (an HTTP agent has no session to add to).
 */
import axios from 'axios';
import { productsApi, cartApi } from '../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Same session-id scheme as CartContext — sharing the key means a tool
// call and the open tab's own UI operate on the exact same guest cart.
function getSessionId() {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
}

// CartContext listens for this to refresh its state — otherwise a tool
// call that changes the cart wouldn't show up in the already-open tab's
// cart badge/sidebar until the next unrelated fetch.
function notifyCartChanged() {
  window.dispatchEvent(new CustomEvent('webmcp:cart-changed'));
}

export function initWebMCP() {
  if (typeof navigator === 'undefined' || !navigator.modelContext
      || typeof navigator.modelContext.provideContext !== 'function') {
    return; // Not supported in this browser — no-op.
  }

  try {
    navigator.modelContext.provideContext({
      tools: [
      {
        name: 'search_products',
        description: 'Search the Cimonamia product catalog by free-text query, with an optional country for pricing.',
        inputSchema: {
          type: 'object',
          properties: {
            query: { type: 'string', description: 'Free-text search query (product name, description, tags)' },
            country: { type: 'string', description: 'ISO 3166-1 alpha-2 country code for pricing/availability, defaults to CO' },
            limit: { type: 'number', description: 'Max results to return, default 10' },
          },
        },
        execute: async ({ query, country, limit } = {}) => {
          const data = await productsApi.getAll({
            search: query || undefined,
            country: country || 'CO',
            limit: limit || 10,
          });
          const products = (data.products || []).map((p) => ({
            name: p.name,
            slug: p.slug,
            price: p.price,
            currency: p.currency,
            url: `${window.location.origin}/product/${p.slug}`,
          }));
          return { total: data.total, products };
        },
      },
      {
        name: 'get_product',
        description: 'Get full details (price, description, sizes, stock) for a single product by its URL slug.',
        inputSchema: {
          type: 'object',
          properties: {
            slug: { type: 'string', description: 'Product URL slug, e.g. from search_products' },
            country: { type: 'string', description: 'ISO 3166-1 alpha-2 country code, defaults to CO' },
          },
          required: ['slug'],
        },
        execute: async ({ slug, country }) => {
          return await productsApi.getBySlug(slug, country || 'CO');
        },
      },
      {
        name: 'shipping_quote',
        description: 'Calculate shipping options and prices for a destination address.',
        inputSchema: {
          type: 'object',
          properties: {
            country: { type: 'string', description: 'ISO 3166-1 alpha-2 country code' },
            state: { type: 'string' },
            city: { type: 'string' },
            postal_code: { type: 'string' },
            street: { type: 'string' },
          },
          required: ['country'],
        },
        execute: async ({ country, state, city, postal_code, street }) => {
          const response = await axios.post(`${API_BASE}/shipping/calculate`, {
            destination: { country, state, city, postal_code, street },
          });
          return response.data;
        },
      },
      {
        name: 'add_to_cart',
        description: "Add a product to the current shopper's cart on this page. If the product has more than one size, `size` is required (see get_product for the list of sizes).",
        inputSchema: {
          type: 'object',
          properties: {
            product_id: { type: 'string', description: 'Product id, from get_product or search_products' },
            quantity: { type: 'number', description: 'Quantity to add, default 1' },
            size: { type: 'string', description: 'Size variant (e.g. "M") — required for multi-size products' },
          },
          required: ['product_id'],
        },
        execute: async ({ product_id, quantity, size }) => {
          const sessionId = getSessionId();
          const result = await cartApi.addToCart(product_id, quantity || 1, size || null, sessionId);
          notifyCartChanged();
          return result;
        },
      },
      ],
    });
  } catch (e) {
    // Experimental API, shape still evolving across browsers — never let
    // it break page load.
    console.warn('WebMCP registration failed:', e);
  }
}
